import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { api, attemptStore, Result } from "../api";
import { foundLine, phraseClue } from "../brand/phrase";
import { warmthFor } from "../brand/warmth";
import { Confetti } from "../components/Confetti";
import { Tally } from "../components/Tally";
import { Wordmark } from "../components/Wordmark";
import { MapView, Pin } from "../components/MapView";

export function ResultScreen() {
  const { pinId = "" } = useParams();
  const navigate = useNavigate();
  const [result, setResult] = useState<Result | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [celebrate, setCelebrate] = useState(false);

  useEffect(() => {
    const attemptId = attemptStore.get(pinId);
    if (!attemptId) {
      setError("No round on this device for that spot.");
      return;
    }
    api
      .result(attemptId)
      .then((r) => {
        setResult(r);
        // Land on the result screen already celebrating.
        if (r.status === "found") setCelebrate(true);
      })
      .catch(() => {
        // Still playing: send them back rather than showing a broken page.
        navigate(`/p/${pinId}`, { replace: true });
      });
  }, [pinId, navigate]);

  if (error) {
    return (
      <div className="page">
        <h1>{error}</h1>
        <p>
          <Link to={`/p/${pinId}`}>Play this spot</Link>
        </p>
      </div>
    );
  }
  if (!result) return <div className="page" style={{ color: "var(--fog)" }}>Loading…</div>;

  const { place } = result;
  const pins: Pin[] = [
    { lat: place.lat, lng: place.lng, label: place.name, kind: "target" },
  ];

  const who = result.hiderName?.split(" ")[0] ?? null;
  const headline =
    result.status === "found"
      ? foundLine(result.guessesUsed, who)
      : result.status === "resigned"
        ? "Gave up"
        : who
          ? `${who} got away`
          : "Not found";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(result.shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div>
      <Confetti fire={celebrate} />
      <div className="topbar">
        <Wordmark size={20} />
        <Link to="/hide" className="btn btn-ghost" >
          Hide a spot
        </Link>
      </div>

      <div className="page">
        <div className="label">{headline}</div>
        {result.status === "found" && (
          <p
            style={{
              fontFamily: "var(--display)",
              fontWeight: 800,
              fontSize: 22,
              letterSpacing: "-0.02em",
              color: "var(--moss)",
              margin: "6px 0 0",
            }}
          >
            {result.guessesUsed === 1 ? "First guess. Clean." : "Got there."}
          </p>
        )}
        <h1 style={{ fontSize: 38, margin: "10px 0 4px" }}>
          {place.name}
          {place.admin1 && place.admin1 !== place.name && (
            <span style={{ color: "var(--fog)" }}>, {place.admin1}</span>
          )}
        </h1>
        <p style={{ color: "var(--fog)", marginTop: 0 }}>
          {place.countryName} · <span className="coord">
            {place.lat.toFixed(3)}, {place.lng.toFixed(3)}
          </span>
        </p>
        <p style={{ maxWidth: 560 }}>{place.blurb}</p>

        {result.note && (
          <div className="reveal-note">
            <div className="label" style={{ marginBottom: 6 }}>
              {result.hiderName ? `${result.hiderName} says` : "The hider says"}
            </div>
            <p style={{ margin: 0, fontSize: 19 }}>{result.note}</p>
          </div>
        )}

        <div
          style={{
            position: "relative",
            height: 300,
            border: "1px solid var(--line)",
            borderRadius: "var(--r-card)",
            overflow: "hidden",
            margin: "22px 0",
          }}
        >
          <MapView pins={pins} fitTo={pins} layerToggle />
        </div>

        <div className="score-grid">
          <div className="score-cell">
            <div className="label">Seeker</div>
            <span className="score-value" style={{ color: "var(--flare)" }}>
              <Tally value={result.seekerScore} duration={1100} />
            </span>
          </div>
          <div className="score-cell">
            <div className="label">Hider</div>
            <span className="score-value" style={{ color: "var(--moss)" }}>
              <Tally value={result.hiderScore} duration={1100} />
            </span>
          </div>
        </div>

        <div style={{ marginTop: 22 }}>
          <div className="stat-row">
            <span>The board</span>
            <span className="num">{result.arena.label}</span>
          </div>
          <div className="stat-row">
            <span>Spent</span>
            <span className="num">{result.spend} / 100</span>
          </div>
          <div className="stat-row">
            <span>Closest look</span>
            {/* The exact distance, finally. Withholding it mid-round was to
                stop trilateration, and there is nothing left to solve. */}
            <span
              className="num"
              style={{
                color: warmthFor(result.bestDistanceKm, result.winRadiusKm).color,
              }}
            >
              {warmthFor(result.bestDistanceKm, result.winRadiusKm).label} ·{" "}
              {result.bestDistanceKm.toLocaleString()} km
            </span>
          </div>
          <div className="stat-row">
            <span>Places looked at</span>
            <span className="num">{result.guessesUsed}</span>
          </div>
          <div className="stat-row">
            <span>Questions answered</span>
            <span className="num">{result.cluesBought.length}</span>
          </div>
        </div>

        {result.cluesBought.length > 0 && (
          <div style={{ marginTop: 26 }}>
            <div className="label" style={{ marginBottom: 8 }}>
              What you paid for
            </div>
            {result.cluesBought.map((clue) => (
              <div className="revealed" key={clue.type}>
                <span className="clue-icon">{clue.icon}</span>
                <div>
                  <div className="clue-sub">{clue.label}</div>
                  <div className="revealed-answer">
                    {phraseClue(clue.type, clue.answer, who)}
                  </div>
                </div>
                <span className="revealed-cost">−{clue.cost}</span>
              </div>
            ))}
          </div>
        )}

        <div style={{ marginTop: 30 }}>
          <div className="label" style={{ marginBottom: 8 }}>
            {who ? `Send this back to ${who}` : "Share it — no spoilers"}
          </div>
          <div className="share-card">{result.shareText}</div>
          <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
            <button className="btn" onClick={copy}>
              {copied ? "Copied" : "Copy share card"}
            </button>
            <Link
              className="btn btn-primary"
              to={who ? `/hide?from=${encodeURIComponent(who)}` : "/hide"}
            >
              {who ? `Hide from ${who} →` : "Rematch. You hide now"}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
